import getVariantFromSelection from '../utils/get-variant-from-selection.js';
import renderSectionFromResponse from '../utils/render-section-from-response.js';
import dispatchEvent from '../utils/dispatch-event.js';
import formatMoney from '../utils/currency.js';

const selectors = {
  sections: {
    productPageTop: '.product-page-top',
  },
  allVariants: '[data-all-variants]',
};

class VariantSelector {
  constructor(element) {
    this.element = element;
    this.topSection = document.querySelector(selectors.sections.productPageTop);
    this.allVariants = JSON.parse(
      document.querySelector(selectors.allVariants).dataset.allVariants
    );
    this.initEventListeners();
    // We assign the original copy of the PDP details accordion here so that we can later inject it back into the page after the 'product-top' section reloads.
    this.detailsAccordion = document.detailsAccordion;
  }

  get currentVariant() {
    return JSON.parse(this.element.dataset.currentVariant);
  }

  get product() {
    return JSON.parse(this.element.dataset.product);
  }

  get variants() {
    return JSON.parse(this.element.dataset.allVariants);
  }

  get variantOptionGroups() {
    return this.element.querySelectorAll('[data-variant-option]');
  }

  switchVariant(option, value, optionIndex) {
    const variant = getVariantFromSelection(
      option,
      value,
      optionIndex,
      this.currentVariant,
      this.variants
    );

    // If we already have that variant selected, then do nothing.
    if (variant.id === this.currentVariant.id) {
      return;
    }

    this.loadInNewVariant(variant.id);
  }

  maybeRenderPreorderForm() {
    const preorderStatus =
      this.topSection.querySelector('[data-preorder-status]').dataset
        .preorderStatus === 'true';

    if (preorderStatus) {
      // eslint-disable-next-line no-unused-expressions
      typeof Globo !== 'undefined' &&
        // eslint-disable-next-line no-undef
        typeof Globo.Preorder !== 'undefined' &&
        // eslint-disable-next-line no-undef
        Globo.Preorder.initPreorder();
      this.initialisePreorderTermsCheckbox();
    }
  }

  initialisePreorderTermsCheckbox() {
    const preorderButton = document.querySelector('.gPreorderBtn');
    const preorderTerms = document.querySelector('.accept-preorder-terms');

    if (!preorderButton) {
      setTimeout(this.initialisePreorderTermsCheckbox, 500);
      return;
    }

    preorderButton.setAttribute('disabled', 'disabled');

    const checkboxContainer = document.createElement('div');
    const checkbox = document.createElement('input');
    const label = document.createElement('label');

    checkboxContainer.classList.add(
      'accept-preorder-terms',
      'flex',
      'prose-a:underline',
      'leading-tight',
      'my-2'
    );

    label.setAttribute('for', 'accept_preorder_terms');
    label.classList.add('pl-1.5', 'text-b5', 'cursor-pointer');
    label.innerHTML = window.translations.cart.general.preorder_terms_html;

    checkbox.type = 'checkbox';
    checkbox.name = 'accept_preorder_terms';
    checkbox.id = 'accept_preorder_terms';
    checkboxContainer.append(checkbox);
    checkboxContainer.append(label);

    checkbox.addEventListener('change', e => {
      if (e.target.checked) {
        preorderButton.removeAttribute('disabled');
      } else {
        preorderButton.setAttribute('disabled', 'disabled');
      }
    });

    if (!preorderTerms) {
      preorderButton.parentElement.insertBefore(
        checkboxContainer,
        preorderButton
      );
    }
  }

  loadInInitialVariant() {
    this.loadInNewVariant(this.currentVariant.id, true);
  }

  loadInNewVariant(id, isFirstLoad = false) {
    if (isFirstLoad) {
      this.topSection.classList.add('pointer-events-none');
    } else {
      this.topSection.classList.add('opacity-50', 'pointer-events-none');
    }

    fetch(`/products/${this.product.handle}?sections=product-top&variant=${id}`)
      .then(response => response.json())
      .then(response => {
        renderSectionFromResponse(
          selectors.sections.productPageTop,
          'product-top',
          response
        );

        this.topSection.classList.remove('opacity-50', 'pointer-events-none');

        this.updateVariantQueryParameter(id);

        this.maybeRenderPreorderForm();

        [...document.querySelectorAll('[data-atc-form-container]')].forEach(
          container => {
            container.classList.remove('invisible', 'h-0', 'overflow-hidden');
          }
        );

        // Here we replace the 'empty' PDP details accordion with the correct version whenever the 'product-top' section reloads.
        let newAccordion = document.querySelector('[data-details-accordion]');
        newAccordion.innerHTML = this.detailsAccordion.innerHTML;

        dispatchEvent('section:product-top:reload', document);
      });
  }

  /**
   * @param {String} sku
   * @returns {Object|null}
   */
  findVariantBySku(sku) {
    return this.variants.find(variant => variant.sku === sku);
  }

  // eslint-disable-next-line class-methods-use-this
  updateVariantQueryParameter(id) {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('variant', id);

    const newurl = `${window.location.protocol}//${window.location.host}${
      window.location.pathname
    }?${searchParams.toString()}`;

    window.history.pushState({ path: newurl }, '', newurl);
  }

  initEventListeners() {
    if (!this.element) {
      return;
    }

    this.element.addEventListener('variant-option:selected', e => {
      this.switchVariant(e.detail.option, e.detail.value, e.detail.index);
    });

    document.addEventListener('variant-sku:selected', e => {
      this.loadInNewVariant(this.findVariantBySku(e.detail.sku)?.id);
    });

    document.addEventListener('variant-gtin:selected', e => {
      // convert GTIN/barcode to SKU
      const gtin = this.allVariants.filter(obj => {
        return obj.barcode === e.detail.gtin;
      });

      this.loadInNewVariant(this.findVariantBySku(gtin[0].sku)?.id);
    });

    this.variantOptionGroups.forEach(group => {
      [...group.querySelectorAll('[data-option-selector]')].forEach(
        selector => {
          selector.addEventListener('click', e => {
            e.preventDefault();

            this.element.dispatchEvent(
              new CustomEvent('variant-option:selected', {
                detail: {
                  option: selector.dataset.option,
                  value: selector.dataset.value,
                  index: Number(selector.dataset.optionIndex),
                },
              })
            );
          });
        }
      );
    });

    const addToCartBtn = document.querySelectorAll('[data-atc-btn]');
    // eslint-disable-next-line
    // const _learnq = window._learnq || [];
    // Update 5/2024: _learnq is deprecated, so switching to klaviyo.push
    const klaviyo = window.klaviyo || [];

    const item = {
      Name: this.product.title,
      ProductID: this.product.id,
      ImageURL: this.product.featured_image,
      URL: window.location.href,
      Brand: this.product.vendor,
      Price: formatMoney(this.product.price),
      CompareAtPrice: formatMoney(this.product.compare_at_price_max),
    };

    addToCartBtn.forEach(btn => {
      // eslint-disable-next-line func-names
      btn.addEventListener('click', function () {
        // _learnq.push(['track', 'Added to Cart', item]);
        klaviyo.push(['track', 'Added to Cart', item]);
      });
    });
  }
}

export default VariantSelector;
